/**
 * Portal form block (ADR-0010 P7b3) — renders an app form inside a page and
 * submits it through the portal. Fetches the public field spec
 * (`portalApi.getForm`), renders an input per field (respecting
 * `show_when`), submits via `portalApi.submitForm`, shows the submit
 * message, then calls `onSubmitted` so a sibling `data_list` refreshes.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, FileText, Upload, X } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { useOutsideClick } from "@/hooks/useOutsideClick";

import { ApiError } from "@/lib/api";
import { type PortalFileValue, type PortalFormField, portalApi } from "@/lib/api-portal";
import { buildFormGroups } from "@/lib/formLayout";
import { validateFieldValue } from "@/lib/formValidation";
import { humanizeColumn } from "@/lib/humanize";

/** Mirrors the backend ``show_when_satisfied``: a field is visible iff every
 *  sibling equals the wanted value(s) (string-compared). */
export function showWhenOk(showWhen: unknown, values: Record<string, unknown>): boolean {
  if (!showWhen || typeof showWhen !== "object") return true;
  for (const [name, wanted] of Object.entries(showWhen as Record<string, unknown>)) {
    const actual = String(values[name] ?? "");
    const list = Array.isArray(wanted) ? wanted : [wanted];
    if (!list.map((w) => String(w)).includes(actual)) return false;
  }
  return true;
}

export function PortalFormBlock({
  appSlug,
  formRef,
  onSubmitted,
  prefill,
}: {
  appSlug: string;
  formRef: string;
  onSubmitted: () => void;
  /** Column-name → value map injected into the submit payload; a prefilled
   *  column is HIDDEN from the form (a form opened for a known parent — e.g.
   *  "abbina al modello corrente" — needn't re-ask it). Blank values ignored. */
  prefill?: Record<string, unknown>;
}) {
  const defQ = useQuery({
    queryKey: ["portal-form-def", appSlug, formRef],
    queryFn: () => portalApi.getForm(appSlug, formRef),
    retry: false,
  });
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Form pro B — per-field inline validation messages (keyed by field name).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (defQ.isLoading) return <p className="text-sm text-muted-foreground">Loading form…</p>;
  if (defQ.error || !defQ.data) {
    return <p className="text-sm text-danger">Form unavailable.</p>;
  }
  const def = defQ.data;
  // A prefilled column is injected into the payload but NOT rendered (the form
  // was opened for a known parent). `show_when` still sees the prefilled value.
  const prefillFilled: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(prefill ?? {})) {
    if (v !== null && v !== undefined && v !== "") prefillFilled[k] = v;
  }
  const prefillKeys = new Set(Object.keys(prefillFilled));
  const effValues = { ...prefillFilled, ...values };
  const visible = def.fields.filter(
    (f) => !prefillKeys.has(f.name) && showWhenOk(f.show_when, effValues),
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Form pro B — validate every visible field before hitting the server.
    const errs: Record<string, string> = {};
    for (const f of visible) {
      const msg = validateFieldValue(f.label, f.validation, values[f.name]);
      if (msg) errs[f.name] = msg;
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { ...prefillFilled };
      for (const f of visible) payload[f.name] = values[f.name] ?? f.default ?? null;
      const res = await portalApi.submitForm(appSlug, formRef, payload);
      setDone(res.submit_message || def.submit_message || "Submitted.");
      setValues({});
      setFieldErrors({});
      onSubmitted();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : "submit failed");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded border border-success/40 bg-success/10 p-3 text-sm">
        {done}
        <button className="ml-3 text-accent" type="button" onClick={() => setDone(null)}>
          New
        </button>
      </div>
    );
  }

  const input =
    "w-full rounded-[var(--portal-radius)] border border-border bg-surface px-2.5 py-2 text-sm outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20";
  // Form pro A — group the (already show-when-filtered) fields into layout
  // sections; a form with no sections collapses to one 1-column group.
  const groups = buildFormGroups(visible, def.sections, def.columns);
  const renderField = (f: PortalFormField) => (
    <div key={f.name} style={f.col_span === 2 ? { gridColumn: "1 / -1" } : undefined}>
      <Field
        field={f}
        value={values[f.name]}
        cls={input}
        error={fieldErrors[f.name] ?? null}
        dependValue={
          // Read from effValues so a PREFILLED + hidden parent (e.g. the model on
          // a "+ Nuovo documento" drawer) still drives the drill-down — otherwise
          // the child stays disabled ("Scegli prima il campo collegato…").
          f.depends_on ? ((effValues[f.depends_on.field] as string | null | undefined) ?? null) : undefined
        }
        onChange={(v) => {
          setValues((s) => {
            const next = { ...s, [f.name]: v };
            // Drill-down: changing a PARENT field clears every child that
            // depends on it (a version of the old model is no longer valid).
            for (const child of def.fields)
              if (child.depends_on?.field === f.name) next[child.name] = null;
            return next;
          });
          // Clear a field's error as soon as the user edits it.
          setFieldErrors((e) => {
            if (!e[f.name]) return e;
            const next = { ...e };
            delete next[f.name];
            return next;
          });
        }}
        onUpload={(file) => portalApi.uploadAttachment(appSlug, formRef, file)}
        onDownload={(v) => portalApi.downloadAttachment(appSlug, v.ref, v.filename)}
        loadOptions={(tableId, s, cols, filter) =>
          portalApi.tableOptions(appSlug, tableId, s, cols, filter)
        }
        loadMembers={() =>
          portalApi.listMembers(appSlug).then((ms) => ms.map((m) => ({ id: m.id, label: m.name })))
        }
      />
    </div>
  );
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      {groups.map((g) => (
        <fieldset key={g.id ?? "__default"} className="flex flex-col gap-3 border-0 p-0">
          {(g.title || g.description) && (
            <div className="border-b border-border pb-1">
              {g.title && (
                <legend className="text-sm font-semibold text-foreground">{g.title}</legend>
              )}
              {g.description && (
                <p className="text-xs text-muted-foreground">{g.description}</p>
              )}
            </div>
          )}
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${g.columns}, minmax(0, 1fr))` }}
          >
            {g.fields.map(renderField)}
          </div>
        </fieldset>
      ))}
      {error && <p className="text-sm text-danger">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        className="w-fit rounded-[var(--portal-radius)] bg-[var(--portal-accent,hsl(var(--accent)))] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
      >
        {busy ? "…" : "Submit"}
      </button>
    </form>
  );
}

export function Field({
  field,
  value,
  cls,
  error,
  onChange,
  onUpload,
  onDownload,
  loadOptions,
  loadMembers,
  initialLabel,
  dependValue,
}: {
  field: PortalFormField;
  value: unknown;
  cls: string;
  /** Form pro B — an inline validation message to show under the control. */
  error?: string | null;
  onChange: (v: unknown) => void;
  /** S5c — for a `file` widget: upload bytes → store the descriptor. */
  onUpload?: (file: File) => Promise<PortalFileValue>;
  onDownload?: (v: PortalFileValue) => void;
  /** For a `relation` widget: fetch the target table's picker options. `columns`
   *  (friendly names) requests a LOOKUP grid — each option then carries `cells`;
   *  `filter` requests a DRILL-DOWN (only rows where `column == value`). */
  loadOptions?: (
    tableId: string,
    search?: string,
    columns?: string[],
    filter?: { column: string; value: string },
  ) => Promise<{ id: string; label: string; cells?: Record<string, unknown> }[]>;
  /** For a `relation` widget with `depends_on`: the current value of the sibling
   *  (parent) field — narrows this picker + locks it while empty. */
  dependValue?: string | null;
  /** For a `member` widget (ADR-0020 P3b): the app's assignable members. */
  loadMembers?: () => Promise<{ id: string; label: string }[]>;
  /** The linked row's label to show before re-pick — a string for a single
   *  `relation`, a parallel list for `relation_many` (one label per id). */
  initialLabel?: string | string[] | null;
}) {
  let control;
  if (field.widget === "file") {
    control = (
      <FileControl value={value} onChange={onChange} onUpload={onUpload} onDownload={onDownload} />
    );
  } else if (field.widget === "relation") {
    control = (
      <RelationControl
        cls={cls}
        targetTable={field.target_table ?? null}
        value={value == null ? "" : String(value)}
        initialLabel={typeof initialLabel === "string" ? initialLabel : null}
        loadOptions={loadOptions}
        lookupColumns={field.lookup_columns ?? null}
        filter={
          field.depends_on && dependValue
            ? { column: field.depends_on.column, value: String(dependValue) }
            : null
        }
        disabled={!!field.depends_on && !dependValue}
        disabledHint={field.depends_on ? "Scegli prima il campo collegato…" : undefined}
        onChange={(id) => onChange(id || null)}
      />
    );
  } else if (field.widget === "relation_many") {
    control = (
      <RelationManyControl
        cls={cls}
        targetTable={field.target_table ?? null}
        value={Array.isArray(value) ? value.map((v) => String(v)) : []}
        initialLabels={Array.isArray(initialLabel) ? initialLabel : null}
        loadOptions={loadOptions}
        onChange={(ids) => onChange(ids)}
      />
    );
  } else if (field.widget === "member") {
    control = (
      <MemberControl
        cls={cls}
        value={value == null ? "" : String(value)}
        loadMembers={loadMembers}
        onChange={(id) => onChange(id || null)}
      />
    );
  } else if (field.widget === "checkbox") {
    control = (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        {humanizeColumn(field.label)}
      </label>
    );
  } else if (field.widget === "number") {
    control = (
      <input type="number" className={cls} value={value == null ? "" : Number(value)} required={field.required} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />
    );
  } else if (field.widget === "select") {
    // Our custom dropdown (NOT a native `<select>`) — same height + look as the
    // text inputs, themed popover. Empty option = unset (only when optional).
    control = (
      <PortalSelect
        cls={cls}
        value={value == null ? "" : String(value)}
        options={field.options ?? []}
        required={field.required}
        onChange={onChange}
      />
    );
  } else if (field.widget === "multiselect") {
    // A chip multi-picker over the configured enum options (NOT a native
    // multi-select). Stores a `string[]`; toggling a chip adds/removes it.
    control = (
      <MultiSelectControl
        options={field.options ?? []}
        value={Array.isArray(value) ? value.map((v) => String(v)) : []}
        onChange={(vals) => onChange(vals)}
      />
    );
  } else if (field.widget === "textarea") {
    control = <textarea className={cls} rows={3} value={String(value ?? "")} required={field.required} onChange={(e) => onChange(e.target.value)} />;
  } else if (
    field.widget === "date" ||
    field.widget === "datetime" ||
    field.widget === "email" ||
    field.widget === "url"
  ) {
    // Form pro A — richer HTML5 inputs: native date/datetime picker + browser
    // email/url validity. A stored ISO timestamp is sliced to the
    // datetime-local shape (YYYY-MM-DDTHH:mm).
    const isDatetime = field.widget === "datetime";
    const raw = String(value ?? "");
    control = (
      <input
        type={isDatetime ? "datetime-local" : field.widget}
        className={cls}
        value={isDatetime ? raw.slice(0, 16) : raw}
        required={field.required}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    control = <input className={cls} value={String(value ?? "")} required={field.required} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className="flex flex-col gap-1">
      {field.widget !== "checkbox" && (
        <label className="text-xs font-medium text-muted-foreground">
          {humanizeColumn(field.label)}
          {field.required && <span className="text-danger"> *</span>}
        </label>
      )}
      {control}
      {error ? (
        <span className="text-xs text-danger">{error}</span>
      ) : (
        field.help && <span className="text-xs text-muted-foreground">{field.help}</span>
      )}
    </div>
  );
}

/** Relation widget: a searchable dropdown of the linked table's rows. Stores
 *  the chosen row id, shows its label. Inert (id only) without `loadOptions`
 *  (e.g. the builder preview). */
function RelationControl({
  cls,
  targetTable,
  value,
  initialLabel,
  loadOptions,
  lookupColumns,
  filter,
  disabled,
  disabledHint,
  onChange,
}: {
  cls: string;
  targetTable: string | null;
  value: string;
  initialLabel: string | null;
  loadOptions?: (
    tableId: string,
    search?: string,
    columns?: string[],
    filter?: { column: string; value: string },
  ) => Promise<{ id: string; label: string; cells?: Record<string, unknown> }[]>;
  /** When non-empty, the popover is a Business-Central-style LOOKUP GRID: a
   *  header + one row per option showing these target columns. Else a plain
   *  single-label dropdown. */
  lookupColumns?: string[] | null;
  /** A DRILL-DOWN filter (dependent picker): restrict options to
   *  `column == value` (e.g. only the versions of the selected model). */
  filter?: { column: string; value: string } | null;
  /** Locked until the parent field is chosen (a drill-down with no value). */
  disabled?: boolean;
  disabledHint?: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [label, setLabel] = useState<string | null>(initialLabel);
  // The record block seeds its row (and thus `initialLabel`) one render AFTER
  // mount, so capturing it only in useState would freeze the label at null and
  // show the raw id. Re-sync whenever it arrives — but never clobber a label
  // the user just picked (that path also drives `value`).
  useEffect(() => {
    if (initialLabel != null) setLabel(initialLabel);
  }, [initialLabel]);
  const wrapRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false), open);
  const cols = lookupColumns && lookupColumns.length ? lookupColumns : undefined;
  const isGrid = !!cols;
  const filterArg = filter && filter.column && filter.value ? filter : undefined;
  const optsQ = useQuery({
    queryKey: [
      "portal-rel-options",
      targetTable,
      search,
      cols?.join(",") ?? "",
      filterArg ? `${filterArg.column}=${filterArg.value}` : "",
    ],
    queryFn: () => loadOptions!(targetTable!, search || undefined, cols, filterArg),
    enabled: open && !!targetTable && !!loadOptions && !disabled,
    retry: false,
  });
  if (!targetTable || !loadOptions) {
    return <input className={cls} disabled value={label || value} />;
  }
  const pick = (o: { id: string; label: string }) => {
    onChange(o.id);
    setLabel(o.label);
    setOpen(false);
    setSearch("");
  };
  const opts = optsQ.data ?? [];
  if (disabled) {
    return (
      <div className={`${cls} cursor-not-allowed text-muted-foreground opacity-60`}>
        {disabledHint || "Select…"}
      </div>
    );
  }
  return (
    <div className="relative" ref={wrapRef}>
      <div className={`${cls} flex items-center justify-between`}>
        <button type="button" className="flex-1 text-left" onClick={() => setOpen((o) => !o)}>
          <span className={value ? "" : "text-muted-foreground"}>
            {label || (value ? value : "Select…")}
          </span>
        </button>
        {value && (
          <button
            type="button"
            className="ml-2 text-muted-foreground"
            onClick={() => {
              onChange("");
              setLabel(null);
            }}
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div
          className={`absolute z-20 mt-1 rounded border border-border bg-surface shadow-lg ${
            isGrid ? "min-w-full w-max max-w-[min(90vw,44rem)]" : "w-full"
          }`}
        >
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca…"
            className="w-full border-b border-border bg-background px-2 py-1.5 text-sm"
          />
          <div className="max-h-64 overflow-auto">
            {isGrid ? (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="sticky top-0 bg-muted/60 text-left text-xs text-muted-foreground">
                    {cols!.map((c) => (
                      <th key={c} className="px-2 py-1.5 font-medium">
                        {humanize(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {opts.map((o) => (
                    <tr
                      key={o.id}
                      className="cursor-pointer border-t border-border/60 hover:bg-background"
                      onClick={() => pick(o)}
                    >
                      {cols!.map((c, i) => (
                        <td key={c} className="whitespace-nowrap px-2 py-1.5">
                          {i === 0 && (o.cells?.[c] == null || o.cells?.[c] === "")
                            ? o.label
                            : cellText(o.cells?.[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              opts.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="block w-full px-2 py-1.5 text-left text-sm hover:bg-background"
                  onClick={() => pick(o)}
                >
                  {o.label}
                </button>
              ))
            )}
            {optsQ.data && opts.length === 0 && (
              <p className="px-2 py-2 text-xs text-muted-foreground">Nessun risultato.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** A friendly column name → a readable header ("modello_nome" → "Modello nome"). */
function humanize(name: string): string {
  const s = name.replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A lookup-grid cell → display text (scalars as-is; null/objects handled). */
function cellText(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** Member (assignee) widget (ADR-0020 P3b): a dropdown of the app's members
 *  (id → display name), member-scoped server-side. Stores the chosen
 *  `app_user` id, shows the name; eager-loads so the current assignee resolves
 *  to a name (the row read carries only the id). Inert without `loadMembers`
 *  (the builder preview). */
function MemberControl({
  cls,
  value,
  loadMembers,
  onChange,
}: {
  cls: string;
  value: string;
  loadMembers?: () => Promise<{ id: string; label: string }[]>;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false), open);
  const membersQ = useQuery({
    queryKey: ["portal-members-picker"],
    queryFn: () => loadMembers!(),
    enabled: !!loadMembers,
    retry: false,
  });
  const members = membersQ.data ?? [];
  const current = members.find((m) => m.id === value);
  if (!loadMembers) {
    return <input className={cls} disabled value={value} />;
  }
  const filtered = search
    ? members.filter((m) => m.label.toLowerCase().includes(search.toLowerCase()))
    : members;
  return (
    <div className="relative" ref={wrapRef}>
      <div className={`${cls} flex items-center justify-between`}>
        <button type="button" className="flex-1 text-left" onClick={() => setOpen((o) => !o)}>
          <span className={value ? "" : "text-muted-foreground"}>
            {current?.label || (value ? value : "Assegna a…")}
          </span>
        </button>
        {value && (
          <button type="button" className="ml-2 text-muted-foreground" onClick={() => onChange("")}>
            ×
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded border border-border bg-surface shadow-lg">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca…"
            className="w-full border-b border-border bg-background px-2 py-1.5 text-sm"
          />
          <div className="max-h-48 overflow-y-auto">
            {filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                className="block w-full px-2 py-1.5 text-left text-sm hover:bg-background"
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {m.label}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-2 py-2 text-xs text-muted-foreground">Nessun membro.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Portal enum dropdown (Stage etc.) — our custom dropdown instead of a native
 *  `<select>`: a trigger styled exactly like the text inputs (`cls`, so same
 *  height/radius/focus) + a themed popover list (`.portal-pop`, outside-click
 *  dismiss, `Check` on the active option). Empty/clear is offered only when the
 *  field is optional. */
function PortalSelect({
  cls,
  value,
  options,
  required,
  onChange,
}: {
  cls: string;
  value: string;
  options: string[];
  required?: boolean;
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false), open);
  const items: { v: string; label: string }[] = [
    ...(required ? [] : [{ v: "", label: "—" }]),
    ...options.map((o) => ({ v: o, label: o })),
  ];
  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${cls} flex items-center justify-between text-left`}
      >
        <span className={value ? "" : "text-muted-foreground"}>{value || "Select…"}</span>
        <ChevronDown
          className={`ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      {open && (
        <div
          role="listbox"
          className="portal-pop absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-[var(--portal-radius)] border border-border bg-surface py-1 shadow-lg"
        >
          {items.map((it) => {
            const sel = it.v === value;
            return (
              <button
                key={it.v || "__empty"}
                type="button"
                role="option"
                aria-selected={sel}
                className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-muted ${
                  it.v ? "" : "text-muted-foreground"
                }`}
                onClick={() => {
                  onChange(it.v || null);
                  setOpen(false);
                }}
              >
                <span>{it.label}</span>
                {sel && it.v && <Check className="h-4 w-4 text-accent" strokeWidth={2} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Multi-select widget: a chip toggle-grid over the configured enum options.
 *  Stores a `string[]`; clicking a chip adds/removes that value. This is the
 *  "belongs to SEVERAL of a fixed set" control (e.g. a document's aree). */
function MultiSelectControl({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
}) {
  const set = new Set(value);
  const toggle = (o: string) => {
    const next = new Set(set);
    if (next.has(o)) next.delete(o);
    else next.add(o);
    // Preserve the option order so the stored array is stable.
    onChange(options.filter((x) => next.has(x)));
  };
  if (options.length === 0)
    return <div className="text-xs text-muted-foreground">Nessuna opzione configurata.</div>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const sel = set.has(o);
        return (
          <button
            key={o}
            type="button"
            aria-pressed={sel}
            onClick={() => toggle(o)}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
              sel
                ? "border-accent bg-accent text-accent-foreground"
                : "border-border bg-surface text-muted-foreground hover:bg-muted"
            }`}
          >
            {sel && <Check className="h-3 w-3" strokeWidth={2.5} />}
            {o}
          </button>
        );
      })}
    </div>
  );
}

/** Relation-many widget: a multi-picker over the linked table. Stores a list
 *  of row ids, renders one labelled chip per id, and resolves new picks'
 *  labels via `loadOptions`. Inert (ids only) without `loadOptions`. */
function RelationManyControl({
  cls,
  targetTable,
  value,
  initialLabels,
  loadOptions,
  onChange,
}: {
  cls: string;
  targetTable: string | null;
  value: string[];
  initialLabels: string[] | null;
  loadOptions?: (tableId: string, search?: string) => Promise<{ id: string; label: string }[]>;
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // id → label cache. Seeded from the parallel initialLabels list (same order
  // as `value`), then grown as the user picks. Re-synced when the row arrives.
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!initialLabels) return;
    setLabels((prev) => {
      const next = { ...prev };
      value.forEach((id, i) => {
        const lbl = initialLabels[i];
        if (lbl != null) next[id] = lbl;
      });
      return next;
    });
  }, [initialLabels, value]);
  const wrapRef = useRef<HTMLDivElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false), open);

  const optsQ = useQuery({
    queryKey: ["portal-rel-many-options", targetTable, search],
    queryFn: () => loadOptions!(targetTable!, search || undefined),
    enabled: open && !!targetTable && !!loadOptions,
    retry: false,
  });

  if (!targetTable || !loadOptions) {
    return <input className={cls} disabled value={value.join(", ")} />;
  }

  const add = (id: string, label: string) => {
    if (!value.includes(id)) onChange([...value, id]);
    setLabels((m) => ({ ...m, [id]: label }));
    setSearch("");
  };
  const remove = (id: string) => onChange(value.filter((v) => v !== id));

  return (
    <div className="relative" ref={wrapRef}>
      <div className={`${cls} flex min-h-[2.25rem] flex-wrap items-center gap-1`}>
        {value.length === 0 && <span className="text-muted-foreground">Select…</span>}
        {value.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent"
          >
            {labels[id] ?? id}
            <button type="button" className="text-accent/70 hover:text-accent" onClick={() => remove(id)}>
              ×
            </button>
          </span>
        ))}
        <button type="button" className="ml-auto text-muted-foreground" onClick={() => setOpen((o) => !o)}>
          +
        </button>
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded border border-border bg-surface shadow-lg">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-full border-b border-border bg-background px-2 py-1.5 text-sm"
          />
          <div className="max-h-48 overflow-y-auto">
            {(optsQ.data ?? [])
              .filter((o) => !value.includes(o.id))
              .map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="block w-full px-2 py-1.5 text-left text-sm hover:bg-background"
                  onClick={() => add(o.id, o.label)}
                >
                  {o.label}
                </button>
              ))}
            {optsQ.data && optsQ.data.filter((o) => !value.includes(o.id)).length === 0 && (
              <p className="px-2 py-2 text-xs text-muted-foreground">No matches.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Narrow a value to a file descriptor (a `file` column's stored shape). */
function asFile(v: unknown): PortalFileValue | null {
  return v && typeof v === "object" && typeof (v as PortalFileValue).ref === "string"
    ? (v as PortalFileValue)
    : null;
}

/** File widget (S5c): upload → descriptor; show the current file with a
 *  download + replace affordance. Inert (plain note) without an uploader. */
function FileControl({
  value,
  onChange,
  onUpload,
  onDownload,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  onUpload?: (file: File) => Promise<PortalFileValue>;
  onDownload?: (v: PortalFileValue) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const current = asFile(value);

  const doUpload = async (file: File | undefined) => {
    if (!file || !onUpload) return;
    setBusy(true);
    try {
      onChange(await onUpload(file));
    } finally {
      setBusy(false);
    }
  };

  // Current file → a clean chip: file icon + name (click to download) + remove.
  if (current) {
    return (
      <div className="flex items-center gap-2 rounded-[var(--portal-radius)] border border-border bg-surface px-3 py-2 text-sm">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left text-foreground hover:underline disabled:no-underline disabled:opacity-70"
          disabled={!onDownload}
          onClick={() => onDownload?.(current)}
          title={current.filename || current.ref}
        >
          {current.filename || current.ref}
        </button>
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
          onClick={() => onChange(null)}
        >
          <X className="h-3.5 w-3.5" />
          Rimuovi
        </button>
      </div>
    );
  }
  if (!onUpload) {
    return <p className="text-xs text-muted-foreground">Caricamento file non disponibile.</p>;
  }
  // Empty → a proper dropzone (click OR drag) instead of the bare native button.
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        disabled={busy}
        onChange={(e) => doUpload(e.target.files?.[0])}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          doUpload(e.dataTransfer.files?.[0]);
        }}
        className={`flex w-full flex-col items-center justify-center gap-1.5 rounded-[var(--portal-radius)] border border-dashed px-4 py-5 text-center text-sm transition disabled:opacity-60 ${
          dragOver
            ? "border-[var(--portal-accent,hsl(var(--accent)))] bg-muted/40"
            : "border-border bg-surface/50 hover:border-muted-foreground/50 hover:bg-muted/30"
        }`}
      >
        <Upload className="h-5 w-5 text-muted-foreground" />
        <span className="font-medium text-foreground">
          {busy ? "Caricamento…" : "Carica un file"}
        </span>
        {!busy && (
          <span className="text-xs text-muted-foreground">Clicca o trascina qui</span>
        )}
      </button>
    </>
  );
}
